import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';

import * as Sentry from '@sentry/react';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY, PRODUCT_DEEP_LINKS } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { ACTIVATE_USER_BY_ACCESS_CODE } from '@/apollo/mutations';

// TODO: Remove after SD-2088 is resolved
const debugLog = (event: string, data: Record<string, unknown>) => {
  Sentry.captureMessage(`[EmailAuthCallback] ${event}`, {
    level: 'info',
    tags: { debug: 'sd-2088' },
    extra: data,
  });
};

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });
  const [activateUserByAccessCode] = useMutation(ACTIVATE_USER_BY_ACCESS_CODE);

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        console.error('[EmailAuthCallback] refreshSession failed:', refreshError?.message);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY)
        || PRODUCT_DEEP_LINKS[product];
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        console.error('[EmailAuthCallback] redirectTo is missing or invalid');
        history.replace('/spark/login');
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        console.error('[EmailAuthCallback] requestAuthCode failed:', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      // TODO: Remove temp debug logging after SD-2088 is resolved
      const debugContext: Record<string, unknown> = {
        fullUrl: window.location.href,
        search: window.location.search,
        hash: window.location.hash ? '[present]' : '[empty]',
        pathname: window.location.pathname,
      };

      debugLog('page_loaded', debugContext);

      try {
        // Wait for Supabase to process the hash fragment (detectSessionInUrl is async)
        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        debugContext.hasSession = !!session;
        debugContext.supabaseUserId = session?.user?.id ?? 'none';
        debugContext.hasLimbixUserId = !!session?.user?.user_metadata?.limbixUserId;
        debugContext.userMetadataKeys = session?.user?.user_metadata
          ? Object.keys(session.user.user_metadata)
          : [];

        if (!session) {
          debugLog('no_session', debugContext);
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';

        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;

        // If access code exists in metadata, this is an activation flow.
        // After activation completes, Vercel clears accessCode from metadata.
        // So returning users won't have it → login handoff.
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        debugContext.entitlementStatus = entitlementStatus;
        debugContext.isReturningUser = isReturningUser;
        debugContext.patientUid = patientUid;
        debugContext.rawAccessCode = rawAccessCode ?? 'NOT IN METADATA';
        debugContext.accessCode = accessCode ?? 'NULL';

        if (isReturningUser) {
          debugLog('returning_user_login_flow', debugContext);
          await handleLoginFlow();
          return;
        }

        // Activation flow: confirm entitlement and activate in Cortex
        let activationResult = 'success';
        try {
          // activateSparkConfirm must run first — it links Supabase user to Cortex.
          // activateUserByAccessCode requires the Cortex patient to exist.
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });

          if (accessCode) {
            await activateUserByAccessCode({
              variables: { accessCode },
              context: { headers: { Authorization: `Bearer ${session.access_token}` } },
            });
          }
        } catch (err) {
          activationResult = `failed: ${err instanceof Error ? err.message : String(err)}`;
          console.error('[EmailAuthCallback] activation failed:', err);
          debugLog('activation_failed', {
            error: err instanceof Error ? err.message : String(err),
            hasAccessCode: !!accessCode,
            supabaseUserId,
            patientUid,
          });
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        debugContext.activationResult = activationResult;
        debugContext.calledActivateUserByAccessCode = !!accessCode;

        debugLog('activation_flow_complete', debugContext);

        // Store redirect target for UserInfoConfirmation session handoff
        const redirectTo = PRODUCT_DEEP_LINKS[product];
        if (redirectTo && isSafeRedirectUri(redirectTo)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
        }

        history.replace(`/activate/${product}/user-info/confirmation`, {
          patientUid,
          flow: 'email',
        });
      } catch (err) {
        console.error('[EmailAuthCallback] Unexpected error:', err);
        debugLog('unexpected_error', {
          ...debugContext,
          error: err instanceof Error ? err.message : String(err),
        });
        history.replace(`/activate/${product}/access-code`, {
          error: 'email_verification_failed',
        });
      }
    };

    handleCallback();
  }, [history, product, fetchMe, activateUserByAccessCode]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
